import { Navigate, Outlet, useRoutes } from 'react-router-dom';

// layouts
import DashboardLayout from './layouts/dashboard/DashboardLayout';
import SimpleLayout from './layouts/simple/SimpleLayout';

// pages
import _ from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EditMyAccountPage from './app/pages/AccountPage/EditMyAccountPage';
import MyAccountPage from './app/pages/AccountPage/MyAccountPage';
import CategoryListPage from './app/pages/CategoryPage/CategoryListPage';
import DepartmentCategoryMobile from './app/pages/CategoryPage/Mobile/DepartmentCategoryMobile';
import EditDepartmentCategoryMobile from './app/pages/CategoryPage/Mobile/EditDepartmentCategoryMobile';
import EditSportingCategoryMobile from './app/pages/CategoryPage/Mobile/EditSportingCategoryMobile';
import EditStatusCategoryMobile from './app/pages/CategoryPage/Mobile/EditStatusCategoryMobile';
import EditTypeCategoryMobile from './app/pages/CategoryPage/Mobile/EditTypeCategoryMobile';
import SportingCategoryMobile from './app/pages/CategoryPage/Mobile/SportingCategoryMobile';
import StatusCategoryMobile from './app/pages/CategoryPage/Mobile/StatusCategoryMobile';
import TypeCategoryMobile from './app/pages/CategoryPage/Mobile/TypeCategoryMobile';
import MapPageWithQueryClient from './app/pages/MapPage/MapPage';
import CreateBulkFacilityPage from './app/pages/FacilityListPage/CreateBulkFacility';
import FacilityListPage from './app/pages/FacilityListPage/FacilityListPage';
import CreateBulkInspectionPage from './app/pages/InspectionListPage/CreateBulkInspection';
import InspectionListPage from './app/pages/InspectionListPage/InspectionListPage';
import LoginFormPage from './app/pages/LoginPage/LoginFormPage';
import AddFacilityFormPage from './app/pages/MapPage/FacilityFormPage/AddFacilityForm';
import EditFacilityFormPage from './app/pages/MapPage/FacilityFormPage/EditFacilityForm';
import SelectAddressPage from './app/pages/MapPage/FacilityFormPage/SelectAddresPage';
import Page404 from './app/pages/Page404';
import CreateUserPage from './app/pages/UserPage/CreateUserPage';
import EditUserPage from './app/pages/UserPage/EditUserPage';
import UserDetailPage from './app/pages/UserPage/UserDetailPage';
import UserListPage from './app/pages/UserPage/UserListPage';
import { selectRefreshToken } from './store/authSlice';
import { REFRESH_USER_SAGA, userSelector } from './store/userSlice';
import { UserRoleList } from './utils/enum';
import EditAddressPage from './app/pages/MapPage/FacilityFormPage/EditAddresPage';

const CatchAllRoutes: any[] = [
  {
    path: '/404',
    element: <SimpleLayout />,
    children: [{ path: '404', element: <Page404 /> }],
  },
  { path: '*', element: <Navigate to="/404" replace /> },
];

const LoggedOutRoutes = [
  {
    path: '/',
    element: <SimpleLayout />,
    children: [
      { path: '/', element: <Navigate to="/login" /> },
      { path: 'login', element: <LoginFormPage /> },
    ],
  },
  {
    path: '/',
    element: <Outlet />,
    children: [
      { element: <Navigate to="login" /> },
      { path: 'map', element: <Navigate to="/login" replace /> },
      { path: 'address', element: <Navigate to="/login" replace /> },
      { path: 'facility', element: <Navigate to="/login" replace /> },
      { path: 'facility/form', element: <Navigate to="/login" replace /> },
      {
        path: 'facility/:facilityId/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'address/:facilityId',
        element: <Navigate to="/login" replace />,
      },
      { path: 'facility/bulk', element: <Navigate to="/login" replace /> },
      { path: 'inspection', element: <Navigate to="/login" replace /> },
      { path: 'inspection/bulk', element: <Navigate to="/login" replace /> },
      { path: 'users', element: <Navigate to="/login" replace /> },
      { path: 'users/:userId', element: <Navigate to="/login" replace /> },
      { path: 'users/form', element: <Navigate to="/login" replace /> },
      { path: 'users/form/:userId', element: <Navigate to="/login" replace /> },
      { path: 'users/me', element: <Navigate to="/login" replace /> },
      { path: 'users/:userId/form', element: <Navigate to="/login" replace /> },
      { path: 'category', element: <Navigate to="/login" replace /> },
      {
        path: 'category/sporting/:subCategoryId/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'category/sporting/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'category/type/:categoryId/form',
        element: <EditTypeCategoryMobile />,
      },
      { path: 'category/type/form', element: <Navigate to="/login" replace /> },
      {
        path: 'category/status/:inspectionResultTypeId/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'category/status/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'category/department/form',
        element: <Navigate to="/login" replace />,
      },
      {
        path: 'category/department/:departmentId/form',
        element: <Navigate to="/login" replace />,
      },
    ],
  },
];

const ManagerLoggedInRoutes = [
  {
    path: '/',
    element: <SimpleLayout />,
    children: [
      { path: '/', element: <Navigate to="/login" /> },
      { path: 'login', element: <LoginFormPage /> },
    ],
  },
  {
    path: '/',
    element: <DashboardLayout />,
    children: [
      { element: <Navigate to="map" /> },
      { path: 'map', element: <MapPageWithQueryClient /> },
      { path: 'address', element: <SelectAddressPage /> },
      { path: 'facility', element: <FacilityListPage /> },
      { path: 'facility/form', element: <AddFacilityFormPage /> },
      {
        path: 'facility/:facilityId/form',
        element: <EditFacilityFormPage />,
      },
      { path: 'address/:facilityId', element: <EditAddressPage /> },
      { path: 'facility/bulk', element: <CreateBulkFacilityPage /> },
      { path: 'inspection', element: <InspectionListPage /> },
      { path: 'inspection/bulk', element: <CreateBulkInspectionPage /> },
      { path: 'users', element: <UserListPage /> },
      { path: 'users/:userId', element: <UserDetailPage /> },
      { path: 'users/form', element: <CreateUserPage /> },
      { path: 'users/form/:userId', element: <EditUserPage /> },
      { path: 'users/me', element: <MyAccountPage /> },
      { path: 'users/:userId/form', element: <EditMyAccountPage /> },
      { path: 'category', element: <CategoryListPage /> },
      {
        path: 'category/sporting/:subCategoryId/form',
        element: <EditSportingCategoryMobile />,
      },
      { path: 'category/sporting/form', element: <SportingCategoryMobile /> },
      {
        path: 'category/type/:categoryId/form',
        element: <EditTypeCategoryMobile />,
      },
      { path: 'category/type/form', element: <TypeCategoryMobile /> },
      {
        path: 'category/status/:inspectionResultTypeId/form',
        element: <EditStatusCategoryMobile />,
      },
      { path: 'category/status/form', element: <StatusCategoryMobile /> },
      {
        path: 'category/department/form',
        element: <DepartmentCategoryMobile />,
      },
      {
        path: 'category/department/:departmentId/form',
        element: <EditDepartmentCategoryMobile />,
      },
    ],
  },
  {
    element: <SimpleLayout />,
    children: [
      { element: <Navigate to="/map" />, index: true },
      { path: '404', element: <Page404 /> },
      { path: '*', element: <Navigate to="/404" /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

const LoggedIndRoutes = [
  {
    path: '/',
    element: <SimpleLayout />,
    children: [
      { path: '/', element: <Navigate to="/login" /> },
      { path: 'login', element: <LoginFormPage /> },
    ],
  },
  {
    path: '/',
    element: <DashboardLayout />,
    children: [
      { element: <Navigate to="map" /> },
      { path: 'map', element: <MapPageWithQueryClient /> },
      { path: 'address', element: <SelectAddressPage /> },
      { path: 'facility', element: <FacilityListPage /> },
      { path: 'facility/form', element: <AddFacilityFormPage /> },
      {
        path: 'facility/:facilityId/form',
        element: <EditFacilityFormPage />,
      },
      { path: 'address/:facilityId', element: <EditAddressPage /> },
      { path: 'facility/bulk', element: <CreateBulkFacilityPage /> },
      { path: 'inspection', element: <InspectionListPage /> },
      { path: 'inspection/bulk', element: <CreateBulkInspectionPage /> },
      { path: 'users/me', element: <MyAccountPage /> },
      { path: 'users/:userId/form', element: <EditMyAccountPage /> },
      { path: 'category', element: <CategoryListPage /> },
      {
        path: 'category/sporting/:subCategoryId/form',
        element: <EditSportingCategoryMobile />,
      },
      { path: 'category/sporting/form', element: <SportingCategoryMobile /> },
      {
        path: 'category/type/:categoryId/form',
        element: <EditTypeCategoryMobile />,
      },
      { path: 'category/type/form', element: <TypeCategoryMobile /> },
      {
        path: 'category/status/:inspectionResultTypeId/form',
        element: <EditStatusCategoryMobile />,
      },
      { path: 'category/status/form', element: <StatusCategoryMobile /> },
      {
        path: 'category/department/form',
        element: <DepartmentCategoryMobile />,
      },
      {
        path: 'category/department/:departmentId/form',
        element: <EditDepartmentCategoryMobile />,
      },
    ],
  },
  {
    element: <SimpleLayout />,
    children: [
      { element: <Navigate to="/map" />, index: true },
      { path: '404', element: <Page404 /> },
      { path: '*', element: <Navigate to="/404" /> },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/404" replace />,
  },
];

export default function Router() {
  const user = useSelector(userSelector);
  const refreshToken = useSelector(selectRefreshToken);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: REFRESH_USER_SAGA });
  }, [dispatch]);

  let routes: any[] = [];
  routes = getUserRoutes(user, refreshToken);

  return useRoutes(routes);
}

function getUserRoutes(user, token) {
  if (!user.id && !token) {
    return LoggedOutRoutes.concat(CatchAllRoutes);
  }
  if (_.isEqual(user?.role, UserRoleList.MANAGER)) {
    return ManagerLoggedInRoutes.concat(LoggedOutRoutes, CatchAllRoutes);
  }

  return LoggedIndRoutes.concat(LoggedOutRoutes, CatchAllRoutes);
}
